import { Component } from '@theme/component';

/**
 * Flytter et popup-link op på samme linje som en variantindstillings label.
 *
 * Variant-pickeren morfer sig selv ved hvert variantskift (variant-picker.js:462) og smider
 * dermed alt, den ikke selv har renderet, ud igen. Derfor holder en MutationObserver øje med
 * pickeren og sætter linket tilbage, hver gang det forsvinder.
 *
 * Er data-option-name tom, eller findes indstillingen ikke, bliver linket stående i blocken.
 */
class LocktownPagePopupComponent extends Component {
  /** @type {Element | null} */
  #anchor = null;
  /** @type {Element | null} */
  #picker = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #scheduled = false;

  connectedCallback() {
    super.connectedCallback();

    this.#anchor = this.querySelector('.locktown-page-popup__anchor');
    if (!this.#anchor) return;

    this.#place();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#picker = null;

    // Linket ligger inde i Shopifys markup. Tag det med, så en slettet block ikke efterlader det.
    if (this.#anchor && !this.contains(this.#anchor)) this.#anchor.remove();
  }

  /**
   * Finder produktsidens variantvælger. Swatches på produktkort i samme sektion springes over.
   *
   * @returns {Element | null}
   */
  #findPicker() {
    const scope = this.closest('.shopify-section, dialog') ?? document;

    for (const picker of scope.querySelectorAll('variant-picker')) {
      if (!picker.closest('product-card')) return picker;
    }

    return null;
  }

  /**
   * Finder den legend, hvis label matcher den valgte variantindstilling.
   *
   * @returns {Element | null}
   */
  #findLabel() {
    const name = (this.dataset.optionName ?? '').trim().toLowerCase();
    if (!name || !this.#picker) return null;

    for (const option of this.#picker.querySelectorAll('.variant-option')) {
      const legend = option.querySelector('legend');
      if (legend && this.#labelText(legend) === name) return legend;
    }

    return null;
  }

  /**
   * Læser labelens egen tekst. Swatch-stilen lægger den valgte værdi i et <span>, som ikke tæller med.
   *
   * @param {Element} element
   * @returns {string}
   */
  #labelText(element) {
    let text = '';

    for (const node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) text += node.nodeValue ?? '';
    }

    return text.trim().toLowerCase();
  }

  #place = () => {
    const anchor = this.#anchor;
    if (!anchor) return;

    if (!this.#picker?.isConnected) this.#observe();

    const label = this.#findLabel();

    if (label) {
      if (anchor.parentElement !== label) label.appendChild(anchor);
    } else if (!this.contains(anchor)) {
      this.appendChild(anchor);
    }
  };

  /** Genfinder pickeren og starter observeren forfra. */
  #observe() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#picker = this.#findPicker();

    if (!this.#picker) return;

    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(this.#picker, { childList: true, subtree: true });
  }

  /** Samler morphens mange mutationer til én placering pr. frame. */
  #schedule = () => {
    if (this.#scheduled) return;
    this.#scheduled = true;

    requestAnimationFrame(() => {
      this.#scheduled = false;
      this.#place();
    });
  };
}

if (!customElements.get('locktown-page-popup-component')) {
  customElements.define('locktown-page-popup-component', LocktownPagePopupComponent);
}
